/**
 * El informe entregado.
 *
 * Dos entradas al mismo documento: la propia (`/informe/:sessionId`) y la del
 * enlace compartido (`/r/:token`), que abre sin sesión iniciada porque el token
 * es la autorización.
 *
 * La diferencia entre ambas no es cosmética. Quien llega por enlace puede ser
 * la pareja de alguien, o el jefe con quien está negociando: ve el diagnóstico
 * porque se lo han enseñado, no porque sea suyo, y la página lo dice.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useEsProbador } from "../components/EntradaAlMapa";
import { Logo } from "../components/Logo";
import { ReportView } from "../components/report/ReportView";
import { BarraSesion } from "../components/Sesion";
import api, { type ReportEnvelope } from "../lib/sessions";

function Marco({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <p className="max-w-prose text-center text-ink/60">{children}</p>
    </div>
  );
}

function Compartir({ token }: { token: string }) {
  const [copiado, setCopiado] = useState(false);
  const url = `${window.location.origin}/r/${token}`;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles queda el campo, que se puede seleccionar
      // a mano. Preferible a no ofrecer nada.
      setCopiado(false);
    }
  };

  return (
    <section className="critero-card">
      <h3 className="mb-2 text-ink">Compartir este informe</h3>
      <p className="mb-4 text-sm text-ink/60">
        Quien tenga este enlace puede leer el informe sin necesidad de cuenta. Compártelo solo con
        quien quieras que lo vea.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="min-w-0 flex-1 rounded-panel border border-line bg-white px-3 py-2 text-sm text-ink/70"
        />
        <button
          type="button"
          onClick={copiar}
          className="critero-boton px-4 py-2"
        >
          {copiado ? "Copiado" : "Copiar enlace"}
        </button>
      </div>
    </section>
  );
}

function Pagina({
  informe,
  compartido,
}: {
  informe: ReportEnvelope;
  compartido: boolean;
}) {
  const fecha = new Date(informe.created_at).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen">
      <header className="no-imprimir mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-8">
        <Link to="/" className="inline-flex">
          <Logo />
        </Link>
        <div className="flex items-center gap-5">
          <span className="critero-label">
            {compartido ? "Informe compartido" : "Tu diagnóstico"} · {fecha}
          </span>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-panel border border-line px-4 py-2 text-sm font-medium text-teal transition hover:bg-paper-2"
          >
            Descargar en PDF
          </button>
          {!compartido ? <BarraSesion /> : null}
        </div>
      </header>

      {/* Cabecera del documento impreso: la de pantalla se oculta, y un informe
          que alguien enseña impreso tiene que identificarse solo. */}
      <div className="hidden print:mb-8 print:block print:border-b print:border-line print:pb-4">
        <p className="font-display text-2xl text-ink">CRITERO</p>
        <p className="mt-1 text-sm text-ink/60">
          Diagnóstico profesional · {fecha} · método {informe.method_version}
        </p>
      </div>

      <main className="mx-auto max-w-6xl px-6 pb-24">
        {compartido ? (
          <p className="no-imprimir mb-8 rounded-tarjeta border border-line bg-paper-2 px-4 py-3 text-sm text-ink/70">
            Estás viendo el informe de otra persona, que ha decidido enseñártelo. No es un
            documento público.
          </p>
        ) : null}

        <ReportView data={informe} ajeno={compartido} />

        {!compartido ? (
          <div className="no-imprimir mt-8">
            <Compartir token={informe.share_token} />
          </div>
        ) : null}

        <p className="mt-10 text-sm text-ink/40">
          Método {informe.method_version} · scoring {informe.scoring_version} · narrativa{" "}
          {informe.narrative_source === "fallback" ? "determinista" : "redactada"}
        </p>
      </main>
    </div>
  );
}

export default function Informe() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const consulta = useQuery({
    queryKey: ["informe", sessionId],
    queryFn: () => api.report(sessionId as string),
    enabled: Boolean(sessionId),
    // Un informe entregado no cambia: no tiene sentido volver a pedirlo al
    // recuperar el foco de la pestaña.
    staleTime: Infinity,
  });

  if (consulta.isLoading) return <Marco>Cargando tu informe…</Marco>;
  if (consulta.isError || !consulta.data) {
    return (
      <Marco>
        No hemos encontrado ese informe. Puede que la sesión aún no esté terminada, o que el enlace
        no sea correcto.
      </Marco>
    );
  }
  return (
    <>
      <Pagina informe={consulta.data} compartido={false} />
      {/*
        La salida del informe hacia la acción.
        
        Solo en la vista propia, no en la compartida: quien abre el enlace de
        otra persona no tiene plan que recorrer — y el `sessionId` de la URL es
        lo que el plan necesita, que allí no existe.
      */}
      <SiguientePaso sessionId={sessionId as string} />
    </>
  );
}

/**
 * El puente del informe a lo que viene después.
 *
 * Sin esto, el plan a 7/30/90 queda como el último bloque de un documento de
 * veintinueve secciones: el sitio exacto donde nadie vuelve después de leerlo
 * una vez. Aquí termina de leer y encuentra por dónde se sigue.
 *
 * Para quien prueba el método, lo que sigue es el Mapa y no el plan. Es el
 * recorrido que fijó Isaac: el diagnóstico abre el Mapa, y del Mapa sale la
 * Misión, que ocupa el sitio del plan por tramos. Mientras se comprueba si es
 * probador no se pinta nada: está al final de un informe largo, así que la
 * respuesta llega antes de que nadie baje hasta aquí, y enseñar el plan para
 * cambiarlo después sería peor que esperar.
 */
export function SiguientePaso({ sessionId }: { sessionId: string }) {
  const probador = useEsProbador();
  if (probador === "comprobando") return null;

  if (probador === "si") {
    return (
      <section className="border-t border-line bg-paper-2">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <h2 className="font-display text-2xl text-ink">Tu siguiente paso es el Mapa</h2>
          <p className="mt-3 max-w-prose text-ink/70">
            Parte de este diagnóstico y lo convierte en decisiones: qué trabajar primero, cómo
            avanzar y cómo cerrar la decisión. El informe se queda aquí y puedes volver cuando
            quieras.
          </p>
          <Link to="/acceso" className="mt-6 inline-block critero-boton">
            Abrir mi Mapa
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="border-t border-line bg-paper-2">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <h2 className="font-display text-2xl text-ink">Ya sabes qué hacer</h2>
        <p className="mt-3 max-w-prose text-ink/70">
          El plan de arriba, repartido en tres tramos y con las acciones marcables. Puedes
          volver cuando quieras: el informe no caduca y el avance se guarda.
        </p>
        <Link
          to={`/plan/${sessionId}`}
          className="mt-6 inline-block critero-boton"
        >
          Ir a mi plan
        </Link>
      </div>
    </section>
  );
}

export function InformeCompartido() {
  const { token } = useParams<{ token: string }>();
  const consulta = useQuery({
    queryKey: ["informe-compartido", token],
    queryFn: () => api.sharedReport(token as string),
    enabled: Boolean(token),
    staleTime: Infinity,
    // Un enlace inventado no se reintenta: es un 404 legítimo, no un fallo de
    // red.
    retry: false,
  });

  if (consulta.isLoading) return <Marco>Abriendo el informe…</Marco>;
  if (consulta.isError || !consulta.data) {
    return <Marco>Este enlace no corresponde a ningún informe.</Marco>;
  }
  return <Pagina informe={consulta.data} compartido />;
}
